/**
 * CaraNudges — proactive Cara suggestion tiles for role dashboards.
 *
 * Fetches up to 3 deterministic, role-scoped nudges from
 * `/api/cara/suggestions` (no LLM call at suggestion time — pure DB
 * reads + small rules). Each tile shows a colored tone stripe (info /
 * warn / error), a one-line title, a one-line body, and two actions:
 *
 *   - "Ask Cara"      → navigates to /ask-cara and prefills the prompt
 *                       via sessionStorage (AskCara reads it on mount
 *                       and immediately sends).
 *   - "<actionLabel>" → direct route to the relevant page.
 *
 * Designed to be mounted at the top of role dashboards as a soft,
 * dismissible row. Component reads CSS variables so light/dark mode
 * tracks the brand automatically.
 *
 * Usage:
 *   <CaraNudges variant="row" />        // horizontal grid for dashboard heads
 *   <CaraNudges variant="stack" />      // vertical for sidebars
 *
 * data-testid root: cara-nudges
 *   per-tile: cara-nudge-{id}, cara-nudge-{id}-ask, cara-nudge-{id}-action
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, AlertTriangle, AlertCircle, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Per-tone styling — drives the left accent stripe + icon color.
// Reads CSS variables so it auto-tracks brand tokens.
const TONE = {
  info:  { accent: 'var(--cn-accent)',  Icon: Sparkles },
  warn:  { accent: 'var(--cn-warning)', Icon: AlertTriangle },
  error: { accent: 'var(--cn-error)',   Icon: AlertCircle },
};

const DISMISSED_KEY = 'cara_nudges_dismissed';

function loadDismissed() {
  try {
    const raw = sessionStorage.getItem(DISMISSED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(set) {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch { /* sessionStorage may be unavailable in some browsers */ }
}

export function CaraNudges({ variant = 'row' }) {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(loadDismissed);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/cara/suggestions`, getAuthHeader());
      setItems(data.items || []);
    } catch (e) {
      // Fail-soft: an empty nudge row is fine; nudges are additive.
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [user, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const visible = items.filter((it) => !dismissed.has(it.id));
  if (!loaded || visible.length === 0) return null;

  const askCara = (item) => {
    try {
      sessionStorage.setItem('cara_prefilled_prompt', item.prompt);
    } catch { /* ignore */ }
    // Dispatch a global event so the in-page CaraDrawer (mounted at the
    // app root) opens with the prompt prefilled. The drawer listens for
    // `cara:open` and reads the same sessionStorage key on its handler.
    // Fallback: if no drawer is mounted on this page (rare — public pages,
    // /ask-cara itself), navigate to the standalone page.
    try {
      window.dispatchEvent(new CustomEvent('cara:open', { detail: { prompt: item.prompt } }));
    } catch { /* CustomEvent unsupported in very old browsers */ }
    // The drawer auto-hides on /ask-cara, /auth, /, /pricing — in those cases
    // navigate so the user still gets a response surface.
    const path = window.location.pathname;
    const drawerHiddenHere = path === '/' || path.startsWith('/auth') || path.startsWith('/ask-cara') || path.startsWith('/pricing') || path.startsWith('/about');
    if (drawerHiddenHere) navigate('/ask-cara');
  };

  const goAction = (item) => navigate(item.action_route);

  const dismiss = (id) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    saveDismissed(next);
  };

  const containerCls = variant === 'stack'
    ? 'flex flex-col gap-3'
    : 'grid gap-3 md:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={containerCls} data-testid="cara-nudges">
      {visible.map((item) => {
        const tone = TONE[item.tone] || TONE.info;
        const Icon = tone.Icon;
        return (
          <div
            key={item.id}
            data-testid={`cara-nudge-${item.id}`}
            className="relative rounded-2xl p-4 flex flex-col gap-3 transition"
            style={{
              background: 'var(--cn-surface)',
              border: '1px solid var(--cn-border)',
              boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            }}
          >
            {/* Tone stripe — uses the brand's semantic colors */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
              style={{ background: tone.accent }}
            />

            <div className="flex items-start gap-3 pl-2">
              <span
                className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(6,182,212,0.08)' }}
              >
                <Icon className="w-4 h-4" style={{ color: tone.accent }} />
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className="text-[10px] uppercase tracking-widest font-bold mb-1"
                  style={{ color: 'var(--cn-text-muted)' }}
                >
                  Cara suggests
                </div>
                <div
                  className="text-sm font-semibold leading-snug"
                  style={{ color: 'var(--cn-text)' }}
                >
                  {item.title}
                </div>
                <div className="text-[12.5px] leading-relaxed mt-1" style={{ color: 'var(--cn-text-muted)' }}>
                  {item.body}
                </div>
              </div>
              <button
                type="button"
                onClick={() => dismiss(item.id)}
                aria-label="Dismiss"
                className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition opacity-60 hover:opacity-100"
                style={{ color: 'var(--cn-text-muted)' }}
                data-testid={`cara-nudge-${item.id}-dismiss`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2 pl-2 flex-wrap">
              <button
                type="button"
                onClick={() => askCara(item)}
                data-testid={`cara-nudge-${item.id}-ask`}
                className="text-xs font-semibold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition"
                style={{
                  background: 'var(--cn-primary)',
                  color: 'var(--cn-surface)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--cn-primary-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--cn-primary)'; }}
              >
                <Sparkles className="w-3.5 h-3.5" />
                Ask Cara
              </button>
              <button
                type="button"
                onClick={() => goAction(item)}
                data-testid={`cara-nudge-${item.id}-action`}
                className="text-xs font-medium rounded-full px-3 py-1.5 inline-flex items-center gap-1 transition"
                style={{
                  background: 'transparent',
                  color: 'var(--cn-text)',
                  border: '1px solid var(--cn-border)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--cn-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--cn-border)'; }}
              >
                {item.action_label}
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default CaraNudges;
